import "@/App.css";
import { lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import Login from "@/pages/Login";
import SetInitialPassword from "@/pages/SetInitialPassword";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const SalesPipeline = lazy(() => import("@/pages/SalesPipeline"));
const Projects = lazy(() => import("@/pages/Projects"));
const AllTasks = lazy(() => import("@/pages/AllTasks"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const Team = lazy(() => import("@/pages/Team"));
const TeamMember = lazy(() => import("@/pages/TeamMember"));
const Profile = lazy(() => import("@/pages/Profile"));
const CalendarView = lazy(() => import("@/pages/CalendarView"));
const ShootSchedule = lazy(() => import("@/pages/ShootSchedule"));
const ShowPerformance = lazy(() => import("@/pages/ShowPerformance"));
const Catalogue = lazy(() => import("@/pages/Catalogue"));
const GuestDirectory = lazy(() => import("@/pages/GuestDirectory"));
const DesignRequests = lazy(() => import("@/pages/DesignRequests"));
const Settings = lazy(() => import("@/pages/Settings"));
const Planner = lazy(() => import("@/pages/Planner"));
const Help = lazy(() => import("@/pages/Help"));

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas">
        <div className="ui-label">Loading control room...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_reset_password) return <SetInitialPassword />;
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ThemedToaster />
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="pipeline" element={<SalesPipeline />} />
            <Route path="projects" element={<Projects />} />
            <Route path="all-tasks" element={<AllTasks />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="team" element={<Team />} />
            <Route path="team/:id" element={<TeamMember />} />
            <Route path="profile" element={<Profile />} />
            <Route path="calendar" element={<CalendarView />} />
            <Route path="shoots" element={<ShootSchedule />} />
            <Route path="shows" element={<ShowPerformance />} />
            <Route path="catalogue" element={<Catalogue />} />
            <Route path="guests" element={<GuestDirectory />} />
            <Route path="design" element={<DesignRequests />} />
            <Route path="settings" element={<Settings />} />
            <Route path="planner" element={<Planner />} />
            <Route path="help" element={<Help />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" />;
}

export default App;
